import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export default function ContactModal({ open, onOpenChange, preset = "General enquiry" }) {
  const [form, setForm] = useState({ name: "", email: "", company: "", role: "", interest: preset, message: "" });

  const submit = (e) => {
    e.preventDefault();
    // Frontend-only: log to console and toast.
    // eslint-disable-next-line no-console
    console.log("[Xyloctra contact request]", form);
    toast.success("Thanks — your Xyloctra team will reach out shortly.", {
      description: `${form.interest} · ${form.company || form.email}`,
    });
    onOpenChange(false);
    setForm({ name: "", email: "", company: "", role: "", interest: preset, message: "" });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0F1424] border-white/10 text-slate-100 sm:max-w-[560px]" data-testid="contact-modal">
        <DialogHeader>
          <div className="tech-label mb-1">CONTACT / XYLOCTRA</div>
          <DialogTitle className="font-heading text-2xl tracking-tight">Talk to our enterprise team</DialogTitle>
          <DialogDescription className="text-slate-400">
            Tell us a little about your organization. A Xyloctra lead will be in touch within one business day.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-400">Name</Label>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 bg-[#0B1021] border-white/10" data-testid="contact-input-name" />
            </div>
            <div>
              <Label className="text-xs text-slate-400">Work email</Label>
              <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 bg-[#0B1021] border-white/10" data-testid="contact-input-email" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-400">Company</Label>
              <Input required value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="mt-1 bg-[#0B1021] border-white/10" data-testid="contact-input-company" />
            </div>
            <div>
              <Label className="text-xs text-slate-400">Role</Label>
              <Input value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="mt-1 bg-[#0B1021] border-white/10" data-testid="contact-input-role" />
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-400">Interest</Label>
            <Select value={form.interest} onValueChange={(v) => setForm({ ...form, interest: v })}>
              <SelectTrigger className="mt-1 bg-[#0B1021] border-white/10" data-testid="contact-select-interest">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#0F1424] border-white/10 text-slate-100">
                <SelectItem value="Revenue Leakage Diagnostic">Revenue Leakage Diagnostic (4–6 wks)</SelectItem>
                <SelectItem value="Commercial Excellence Workshop">Commercial Excellence Workshop</SelectItem>
                <SelectItem value="Board Intelligence Overview">Board Intelligence Overview</SelectItem>
                <SelectItem value="Controlled Pilot">Controlled Pilot (12–18 wks)</SelectItem>
                <SelectItem value="Enterprise Rollout">Enterprise Rollout</SelectItem>
                <SelectItem value="General enquiry">General enquiry</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-slate-400">Notes</Label>
            <Textarea rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1 bg-[#0B1021] border-white/10" placeholder="Anything specific about your data landscape, geography, or goals" data-testid="contact-input-message" />
          </div>
          <DialogFooter>
            <button type="button" onClick={() => onOpenChange(false)} className="px-4 py-2 rounded-md text-sm btn-ghost" data-testid="contact-cancel">Cancel</button>
            <button type="submit" className="px-5 py-2 rounded-md text-sm btn-primary" data-testid="contact-submit">Request contact</button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
